import {
  reconcileAduReportAcquisitionIdentity,
  resolveDurableReportMessageKey,
  resolvePublicSurfacePath,
} from '../config/publicSurfaces';
import {
  getConsentSnapshot,
  runWhenConsentReady,
  whenConsentSettled,
} from '../privacy/cookiebot';

const FIRST_PARTY_FUNNEL_ENDPOINT = '/api/recordFunnelEvent';
// Funnel events are consent-NEUTRAL — they always send, and record consent state
// as fields. That is exactly why the fields have to be right: an event built
// before Cookiebot answers stamps `provider_pending` on a visitor who never made
// a choice. So an event raised during that window waits for the provider's
// answer — on a budget set by WHAT RAISED IT, because the two kinds of event
// have nothing in common but the endpoint.
//
// Mount-time events are raised by a render, which on a cold pageview is always
// before the provider answers, and nothing downstream is blocking on them.
// Production evidence (2026-09-02, adulab.app, cold first pageviews): 3 of 6
// `landing_loaded` events still read `provider_pending` — uc.js, its consentcdn
// settings fetch, and the bc-v4 iframe routinely need more than a second on a
// fresh visit, which made the consent fields on first-party landing and section
// events a coin flip. The budget below is wide enough to cover that load.
const FUNNEL_MOUNT_CONSENT_WAIT_MS = 6000;
// User-action events sit in front of somebody mid-flow — a click, a submit, a
// checkout session — so they keep the short checkout-grade budget. Waiting a
// buyer out for better metadata is the trade this side must never make.
const FUNNEL_ACTION_CONSENT_WAIT_MS = 1500;
// Funnel events raised by a render or a passive gesture rather than by a user's
// intent. Everything not listed here is treated as a user action.
const MOUNT_TIME_FUNNEL_EVENTS = new Set([
  'landing_loaded',
  'pricing_loaded',
  'section_viewed',
  'scroll_depth',
  // /welcome is a landing surface in the variant-C arm: its gate renders from a
  // mount effect and is often the first paint of the visit.
  'welcome_gate_viewed',
]);
const STORAGE_PREFIX = 'adu_utm_';
const ATTRIBUTION_SESSION_ID_KEY = `${STORAGE_PREFIX}attribution_session_id`;
const FIRST_PARTY_FUNNEL_EVENTS = new Set([
  'landing_loaded',
  'pricing_loaded',
  'cta_clicked',
  'sample_report_viewed',
  'scroll_depth',
  'checkout_clicked',
  'checkout_session_created',
  'assessment_started',
  'assessment_completed',
  'assessment_to_pricing',
  // Report conversion FLOW stage band (variants A–D): ZIP check → reveal →
  // gate → checkout, joined per flow arm via flow_key/flow_version.
  'property_check_started',
  'property_check_completed',
  'property_check_failed',
  'results_gate_shown',
  'lead_email_submitted',
  'results_revealed',
  'welcome_gate_viewed',
  'welcome_gate_submitted',
  // Section-level visibility on landing surfaces (server already allow-lists
  // this event; it was unreachable client-side until 2026-08-27).
  'section_viewed',
]);
const FIRST_PARTY_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const FIRST_PARTY_CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'];
const PRODUCTION_HOSTS = new Set(['adulab.app', 'www.adulab.app']);
const FIRST_PARTY_SAFE_PARAM_KEYS = new Set([
  'surface',
  'placement',
  'tier',
  'checkout_source',
  'checkoutSessionId',
  'checkout_session_id',
  'ctaId',
  'cta_id',
  'ctaDestination',
  'cta_text',
  'cta_destination',
  'scroll_depth',
  'section',
  'value',
  'currency',
  'attributionSessionId',
  'attribution_session_id',
  'variant',
  'messageKey',
  'message_key',
  'messageKeyApplied',
  'message_key_applied',
  'productKey',
  'product_key',
  'cartridgeKey',
  'cartridge_key',
  'surfaceKey',
  'surface_key',
  'experienceDisplayName',
  'experience_display_name',
  'templateId',
  'template_id',
  'landingId',
  'landing_id',
  'destinationId',
  'destination_id',
  'flowKey',
  'flow_key',
  'flowVersion',
  'flow_version',
  'checkStatus',
  'check_status',
  'checkRequestId',
  'check_request_id',
  'editionId',
  'edition_id',
  'renderEngine',
  'render_engine',
  'assessmentId',
  'assessment_id',
  'publicToken',
  'public_token',
]);
const FIRST_PARTY_PARAM_ALIASES = {
  checkoutSessionId: 'checkout_session_id',
  attributionSessionId: 'attribution_session_id',
  messageKey: 'message_key',
  messageKeyApplied: 'message_key_applied',
  productKey: 'product_key',
  cartridgeKey: 'cartridge_key',
  surfaceKey: 'surface_key',
  experienceDisplayName: 'experience_display_name',
  templateId: 'template_id',
  landingId: 'landing_id',
  destinationId: 'destination_id',
  flowKey: 'flow_key',
  flowVersion: 'flow_version',
  checkStatus: 'check_status',
  checkRequestId: 'check_request_id',
  editionId: 'edition_id',
  renderEngine: 'render_engine',
  assessmentId: 'assessment_id',
  publicToken: 'public_token',
  ctaId: 'cta_id',
  ctaDestination: 'cta_destination',
};
const REPORT_ACQUISITION_PARAM_KEYS = Object.freeze([
  'product_key',
  'cartridge_key',
  'surface_key',
  'experience_display_name',
  'template_id',
  'landing_id',
  'destination_id',
  'render_engine',
  'message_key',
  'message_key_applied',
]);

function normalizeString(value, maxLength = 500) {
  const normalized = String(value).trim();
  return normalized ? normalized.slice(0, maxLength) : undefined;
}

function readViteEnv(name) {
  try {
    const value = import.meta?.env?.[name];
    return typeof value === 'string' ? value.trim() : '';
  } catch {
    return '';
  }
}

export function sanitizeMarketingEventParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params)
      .map(([key, value]) => {
        if (value === undefined || value === null) return [key, undefined];
        if (typeof value === 'string') return [key, normalizeString(value)];
        if (Array.isArray(value)) return [key, value.length ? value : undefined];
        return [key, value];
      })
      .filter(([, value]) => value !== undefined)
  );
}

function pageContext() {
  try {
    return {
      page_path: window.location?.pathname,
      page_location: window.location?.href,
      page_title: document?.title,
    };
  } catch {
    return {};
  }
}

function readSessionStorage(key) {
  try {
    return window.sessionStorage?.getItem(key) || '';
  } catch {
    return '';
  }
}

function reconcileReportIdentityParams(params = {}) {
  const reconciliation = reconcileAduReportAcquisitionIdentity({
    productKey: params.product_key,
    cartridgeKey: params.cartridge_key,
    surfaceKey: params.surface_key,
    experienceDisplayName: params.experience_display_name,
    templateId: params.template_id,
    landingId: params.landing_id,
    destinationId: params.destination_id,
    renderEngine: params.render_engine,
    messageKey: params.message_key,
    messageKeyApplied: params.message_key_applied,
  });
  if (!reconciliation.isReportIdentityClaim) return params;

  const reconciled = { ...params };
  for (const key of REPORT_ACQUISITION_PARAM_KEYS) {
    delete reconciled[key];
  }

  if (!reconciliation.identity) {
    delete reconciled.flow_key;
    delete reconciled.flow_version;
    return reconciled;
  }
  const identity = reconciliation.identity;
  return {
    ...reconciled,
    product_key: identity.productKey,
    cartridge_key: identity.cartridgeKey,
    surface_key: identity.surfaceKey,
    experience_display_name: identity.experienceDisplayName,
    template_id: identity.templateId,
    landing_id: identity.landingId,
    destination_id: identity.destinationId,
    render_engine: identity.renderEngine,
    message_key: identity.messageKey,
    message_key_applied: identity.messageKeyApplied,
  };
}

function writeSessionStorage(key, value) {
  try {
    window.sessionStorage?.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function getAttributionSessionId() {
  const existing = readSessionStorage(ATTRIBUTION_SESSION_ID_KEY);
  if (existing) return existing;

  let id = '';
  try {
    id = window.crypto?.randomUUID ? `attr_${window.crypto.randomUUID()}` : '';
  } catch {
    id = '';
  }

  if (!id) {
    id = `attr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  writeSessionStorage(ATTRIBUTION_SESSION_ID_KEY, id);
  return id;
}

function readCurrentUtmParams() {
  try {
    const search = window.location?.search || '';
    const urlParams = new URLSearchParams(search);
    return Object.fromEntries(
      FIRST_PARTY_UTM_KEYS
        .map((key) => [
          key,
          normalizeString(urlParams.get(key) || readSessionStorage(`adu_utm_${key}`)),
        ])
        .filter(([, value]) => value)
    );
  } catch {
    return {};
  }
}

function readClickIds() {
  try {
    const search = window.location?.search || '';
    const urlParams = new URLSearchParams(search);
    return Object.fromEntries(
      FIRST_PARTY_CLICK_ID_KEYS
        .map((key) => [
          key,
          normalizeString(urlParams.get(key) || readSessionStorage(`${STORAGE_PREFIX}${key}`), 240),
        ])
        .filter(([, value]) => value)
    );
  } catch {
    return {};
  }
}

function isInternalTest(utmParams = {}) {
  try {
    const hostname = window.location?.hostname || '';
    if (hostname && !PRODUCTION_HOSTS.has(hostname)) return true;
  } catch {
    /* ignore */
  }

  return Object.values(utmParams)
    .some((value) => String(value || '').toLowerCase().includes('codex'));
}

// The ONLY fields a deferred funnel event may resolve late. `internalTest` is
// derived from the page the event was raised on, so it is passed in frozen
// rather than re-read here.
function consentEventFields(internalTest) {
  try {
    const snapshot = getConsentSnapshot({ internalTest });
    return sanitizeMarketingEventParams({
      marketing_consent: snapshot.marketing_consent,
      do_not_sell: snapshot.do_not_sell,
      gpc: snapshot.gpc,
      internal_test: snapshot.internal_test,
      ad_upload_eligible: snapshot.ad_upload_eligible,
      upload_blocked_reason: snapshot.upload_blocked_reason,
    });
  } catch {
    return {};
  }
}

function getReferrerHost() {
  try {
    const referrer = document?.referrer || '';
    if (!referrer) return undefined;
    return new URL(referrer).hostname;
  } catch {
    return undefined;
  }
}

// Campaign join spine captured by utm.js (captureUtmParams). Read from the shared
// adu_utm_* sessionStorage keys so every first-party funnel event — not just the
// ones whose call sites pass attribution explicitly — carries variant and the
// message-match arm alongside attribution_session_id (added in adUploadState).
function storedAttributionSpine() {
  const spine = {};
  const variant = readSessionStorage(`${STORAGE_PREFIX}variant`);
  if (variant) spine.variant = variant;
  const messageKey = readSessionStorage(`${STORAGE_PREFIX}message_key`);
  if (messageKey) {
    spine.message_key =
      resolveDurableReportMessageKey(messageKey) || messageKey;
  }
  const messageKeyApplied = readSessionStorage(`${STORAGE_PREFIX}message_key_applied`);
  if (messageKeyApplied) {
    spine.message_key_applied =
      resolveDurableReportMessageKey(messageKeyApplied) || messageKeyApplied;
  }
  for (const key of [
    'product_key',
    'cartridge_key',
    'surface_key',
    'experience_display_name',
    'template_id',
    'landing_id',
    'destination_id',
    'flow_key',
    'flow_version',
    'edition_id',
    'render_engine',
  ]) {
    const value = readSessionStorage(`${STORAGE_PREFIX}${key}`);
    if (value) spine[key] = value;
  }
  return reconcileReportIdentityParams(spine);
}

function currentPublicSurfaceSpine() {
  try {
    const resolved = resolvePublicSurfacePath(window.location?.pathname || '');
    if (!resolved) return {};

    return {
      product_key: resolved.productKey,
      cartridge_key: resolved.cartridgeKey,
      surface_key: resolved.surfaceKey,
      experience_display_name: resolved.experienceDisplayName,
      template_id: resolved.templateId,
      landing_id: resolved.landingId,
      destination_id: resolved.destinationId,
      render_engine: resolved.renderEngine,
      message_key: resolved.messageKey,
      message_key_applied: resolved.messageKeyApplied,
    };
  } catch {
    return {};
  }
}

// Every field here is read from window.location, the resolved route, the DOM, or
// same-tab storage — all of which move the instant the SPA navigates. Callers
// must evaluate this at event time, never after a deferral.
function firstPartyPageContext(utmParams = readCurrentUtmParams()) {
  try {
    return sanitizeMarketingEventParams({
      page_path: window.location?.pathname,
      page_host: window.location?.hostname,
      referrer_host: getReferrerHost(),
      ...utmParams,
      ...storedAttributionSpine(),
      // A valid route outranks stale same-tab attribution storage.
      ...currentPublicSurfaceSpine(),
      ...readClickIds(),
      attribution_session_id: getAttributionSessionId(),
    });
  } catch {
    return {};
  }
}

function firstPartyEventParams(params = {}) {
  const sanitized = sanitizeMarketingEventParams(params);
  let payload = {};

  for (const [key, value] of Object.entries(sanitized)) {
    if (!FIRST_PARTY_SAFE_PARAM_KEYS.has(key)) continue;
    const outputKey = FIRST_PARTY_PARAM_ALIASES[key] || key;
    payload[outputKey] = value;
  }

  if (payload.cta_destination) {
    payload.cta_destination = firstPartyPathOnly(payload.cta_destination);
  }

  for (const key of ['message_key', 'message_key_applied']) {
    if (!payload[key]) continue;
    payload[key] = resolveDurableReportMessageKey(payload[key]) || payload[key];
  }
  payload = reconcileReportIdentityParams(payload);

  if (typeof payload.currency === 'string') {
    payload.currency = payload.currency.trim().toUpperCase().slice(0, 10);
  }

  if (payload.value !== undefined) {
    const numericValue = Number(payload.value);
    if (Number.isFinite(numericValue)) {
      payload.value = numericValue;
    } else {
      delete payload.value;
    }
  }

  if (payload.scroll_depth !== undefined) {
    const numericDepth = Number(payload.scroll_depth);
    if (Number.isFinite(numericDepth) && numericDepth >= 0 && numericDepth <= 100) {
      payload.scroll_depth = Math.round(numericDepth);
    } else {
      delete payload.scroll_depth;
    }
  }

  return payload;
}

function firstPartyPathOnly(value) {
  try {
    const normalized = normalizeString(value);
    if (!normalized) return undefined;
    const url = normalized.startsWith('/')
      ? new URL(normalized, 'https://adulab.app')
      : new URL(normalized);
    if (!['adulab.app', 'www.adulab.app'].includes(url.hostname)) return undefined;
    return url.pathname;
  } catch {
    return undefined;
  }
}

const deferredFunnelEvents = [];
let deferredFunnelFlushBound = false;

function flushDeferredFunnelEvents() {
  // Splice first: a send that throws must not strand the rest of the queue, and
  // a flush racing another flush must not send anything twice.
  deferredFunnelEvents.splice(0).forEach((send) => {
    try {
      send();
    } catch {
      /* Measurement failures never interrupt application behavior. */
    }
  });
}

function consentWaitBudgetMs(eventName) {
  return MOUNT_TIME_FUNNEL_EVENTS.has(eventName)
    ? FUNNEL_MOUNT_CONSENT_WAIT_MS
    : FUNNEL_ACTION_CONSENT_WAIT_MS;
}

function bindDeferredFunnelFlush() {
  if (deferredFunnelFlushBound) return;
  if (typeof window.addEventListener !== 'function') return;
  deferredFunnelFlushBound = true;
  // The reliability floor: pagehide is the last moment sendBeacon is still
  // honored, so a buyer who navigates inside the wait window still reports the
  // event — with whatever consent state is current then, rather than not at all.
  window.addEventListener('pagehide', flushDeferredFunnelEvents);
}

/**
 * Snapshot the whole event except its consent fields, synchronously, at the
 * moment it is raised.
 *
 * A CTA click on /report/<slug> hands the SPA /checkout/report in the same tick.
 * A deferred send that re-derived page_path, the surface spine, the message key,
 * or the destination id would stamp the checkout page onto the event that left
 * the landing page — silently reattributing the funnel step that matters most.
 * So everything route-, DOM-, and storage-derived is frozen here, and only the
 * consent fields (which are the entire reason for the wait) resolve later.
 */
function freezeFunnelEvent(eventName, params) {
  const utmParams = readCurrentUtmParams();
  return {
    internalTest: isInternalTest(utmParams),
    payload: reconcileReportIdentityParams({
      event_name: eventName,
      ...firstPartyPageContext(utmParams),
      ...firstPartyEventParams(params),
    }),
  };
}

function sendFunnelEvent({ payload, internalTest }) {
  try {
    const body = JSON.stringify({
      ...payload,
      ...consentEventFields(internalTest),
    });

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(FIRST_PARTY_FUNNEL_ENDPOINT, body);
    }

    if (typeof fetch === 'function') {
      void fetch(FIRST_PARTY_FUNNEL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
      return true;
    }
  } catch {
    return false;
  }

  return false;
}

export function trackFunnelEvent(eventName, params = {}) {
  try {
    if (!FIRST_PARTY_FUNNEL_EVENTS.has(eventName)) return false;
    if (typeof window === 'undefined') return false;

    const frozen = freezeFunnelEvent(eventName, params);
    const send = () => sendFunnelEvent(frozen);
    if (getConsentSnapshot().provider_status !== 'pending') return send();

    deferredFunnelEvents.push(send);
    bindDeferredFunnelFlush();
    // Ordering rule: ONE arrival-ordered queue, drained as a unit by whichever
    // wait lands first — provider readiness, or the shortest budget still
    // outstanding. So a cta_clicked raised while a landing_loaded is still
    // waiting can never overtake it: the CTA's own 1.5s budget flushes the whole
    // queue in arrival order, sending the earlier mount event with whatever
    // state it has at that moment. A late event only ever SHORTENS an earlier
    // one's wait, never reorders it, and no event waits past its own budget.
    // (`whenConsentSettled` races readiness against the budget, so a provider
    // that answers early resolves everything immediately rather than sleeping.)
    void whenConsentSettled(consentWaitBudgetMs(eventName)).then(flushDeferredFunnelEvents);
    return true;
  } catch {
    return false;
  }
}

export function trackLandingLoaded(params = {}) {
  return trackFunnelEvent('landing_loaded', params);
}

export function trackPricingLoaded(params = {}) {
  return trackFunnelEvent('pricing_loaded', params);
}

export function trackCheckoutClicked(params = {}) {
  return trackFunnelEvent('checkout_clicked', params);
}

export function trackSectionViewed({ section, surface, messageKey, flowKey, flowVersion } = {}) {
  if (!section) return false;
  return trackFunnelEvent('section_viewed', {
    section,
    surface,
    messageKey,
    flowKey,
    flowVersion,
  });
}

export function trackCampaignCtaClick({
  ctaId,
  ctaText,
  destination,
  placement,
  surface,
  tier,
  messageKey,
  messageKeyApplied,
  flowKey,
  flowVersion,
  editionId,
} = {}) {
  const firstPartyDestination = firstPartyPathOnly(destination);
  const firstPartyTracked = trackFunnelEvent('cta_clicked', {
    cta_id: ctaId,
    cta_destination: firstPartyDestination,
    placement,
    surface,
    tier,
    messageKey,
    messageKeyApplied,
    flowKey,
    flowVersion,
    editionId,
  });
  const ga4Tracked = trackCtaClick({
    ctaId,
    ctaText,
    destination: firstPartyDestination || destination,
    placement,
    surface,
    tier,
    messageKey,
    messageKeyApplied,
    flowKey,
    flowVersion,
    editionId,
  });
  return firstPartyTracked || ga4Tracked;
}

export function trackSampleReportViewed({
  placement,
  surface,
  tier,
} = {}) {
  const firstPartyTracked = trackFunnelEvent('sample_report_viewed', {
    placement,
    surface,
    tier,
  });
  const ga4Tracked = trackGa4Event('view_sample_report', {
    placement,
    surface,
    tier,
  });
  return firstPartyTracked || ga4Tracked;
}

export function trackCampaignScrollDepth({
  depth,
  surface,
  messageKey,
  flowKey,
  flowVersion,
} = {}) {
  const numericDepth = Number(depth);
  if (!Number.isFinite(numericDepth) || numericDepth < 0 || numericDepth > 100) {
    return false;
  }

  const scrollDepth = Math.round(numericDepth);
  const firstPartyTracked = trackFunnelEvent('scroll_depth', {
    scroll_depth: scrollDepth,
    surface,
    messageKey,
    flowKey,
    flowVersion,
  });
  const ga4Tracked = trackGa4Event('scroll_depth', {
    percent_scrolled: scrollDepth,
    surface,
    message_key: messageKey,
    flow_key: flowKey,
    flow_version: flowVersion,
  });
  return firstPartyTracked || ga4Tracked;
}

export function trackFirstPartyCheckoutSessionCreated(params = {}) {
  return trackFunnelEvent('checkout_session_created', params);
}

export function trackAssessmentStarted(params = {}) {
  return trackFunnelEvent('assessment_started', params);
}

export function trackAssessmentCompleted(params = {}) {
  return trackFunnelEvent('assessment_completed', params);
}

export function trackAssessmentToPricing(params = {}) {
  return trackFunnelEvent('assessment_to_pricing', params);
}

// Report conversion FLOW stage wrappers (variants A–D). Call sites pass the
// flow join spine ({ flowKey, flowVersion, messageKey, ... }); the safe-param
// filter strips everything else, so lead identity (email/name) can never ride
// along. Submitting the lead gate records the ACT only — it is not a
// marketing-consent grant and must never touch consent state.
export function trackPropertyCheckStarted(params = {}) {
  return trackFunnelEvent('property_check_started', params);
}

export function trackPropertyCheckCompleted(params = {}) {
  return trackFunnelEvent('property_check_completed', params);
}

export function trackPropertyCheckFailed(params = {}) {
  return trackFunnelEvent('property_check_failed', params);
}

export function trackResultsGateShown(params = {}) {
  return trackFunnelEvent('results_gate_shown', params);
}

export function trackLeadEmailSubmitted(params = {}) {
  return trackFunnelEvent('lead_email_submitted', params);
}

export function trackResultsRevealed(params = {}) {
  return trackFunnelEvent('results_revealed', params);
}

export function trackWelcomeGateViewed(params = {}) {
  return trackFunnelEvent('welcome_gate_viewed', params);
}

export function trackWelcomeGateSubmitted(params = {}) {
  return trackFunnelEvent('welcome_gate_submitted', params);
}

export function trackGa4Event(eventName, params = {}, options = {}) {
  try {
    if (!eventName || typeof window === 'undefined') return false;
    const requiredConsent = options.adPlatform
      ? ['analytics', 'marketing']
      : 'analytics';
    return runWhenConsentReady(requiredConsent, () => {
      if (typeof window.gtag !== 'function') return false;
      window.gtag('event', eventName, sanitizeMarketingEventParams({
        ...pageContext(),
        ...params,
      }));
      return true;
    });
  } catch {
    return false;
  }
}

export function getGoogleAdsSendTo(label) {
  const conversionId = readViteEnv('VITE_GOOGLE_ADS_CONVERSION_ID');
  const normalizedLabel = normalizeString(label);
  if (!conversionId || !normalizedLabel) return '';
  return `${conversionId}/${normalizedLabel}`;
}

export function trackGoogleAdsConversion({
  label,
  value = 1,
  currency = 'USD',
  transactionId,
} = {}) {
  try {
    if (typeof window === 'undefined') return false;

    const sendTo = getGoogleAdsSendTo(label);
    if (!sendTo) return false;

    return runWhenConsentReady('marketing', () => {
      if (typeof window.gtag !== 'function') return false;
      window.gtag('event', 'conversion', sanitizeMarketingEventParams({
        send_to: sendTo,
        value,
        currency,
        transaction_id: transactionId,
      }));
      return true;
    });
  } catch {
    return false;
  }
}

export function trackGoogleAdsPurchaseConversion({ eventId, value, currency = 'USD' } = {}) {
  return trackGoogleAdsConversion({
    label: readViteEnv('VITE_GOOGLE_ADS_PURCHASE_LABEL'),
    value,
    currency,
    transactionId: eventId,
  });
}

export function trackMicrosoftUetEvent({
  action,
  category,
  label,
  value,
  currency = 'USD',
  eventId,
} = {}) {
  try {
    if (!action) return false;
    return runWhenConsentReady('marketing', () => {
      if (!window.uetq || typeof window.uetq.push !== 'function') return false;
      window.uetq.push('event', action, sanitizeMarketingEventParams({
        event_category: category,
        event_label: label,
        event_value: value,
        revenue_value: value,
        currency,
        event_id: eventId,
      }));
      return true;
    });
  } catch {
    return false;
  }
}

export function trackMicrosoftUetPurchaseConversion({ eventId, value, currency = 'USD' } = {}) {
  return trackMicrosoftUetEvent({
    action: 'purchase',
    category: 'checkout',
    label: eventId,
    value,
    currency,
    eventId,
  });
}

export function trackRedditPixelEvent(eventName, {
  value,
  currency = 'USD',
  transactionId,
} = {}) {
  try {
    if (!eventName) return false;
    return runWhenConsentReady('marketing', () => {
      if (typeof window.rdt !== 'function') return false;
      window.rdt('track', eventName, sanitizeMarketingEventParams({
        value,
        currency,
        conversionId: transactionId,
      }));
      return true;
    });
  } catch {
    return false;
  }
}

export function trackRedditPixelPurchaseConversion({ eventId, value, currency = 'USD' } = {}) {
  return trackRedditPixelEvent('Purchase', {
    value,
    currency,
    transactionId: eventId,
  });
}

export function trackGoogleAdsLeadFormConversion({ transactionId, value = 1, currency = 'USD' } = {}) {
  return trackGoogleAdsConversion({
    label: readViteEnv('VITE_GOOGLE_ADS_LEAD_FORM_LABEL'),
    value,
    currency,
    transactionId,
  });
}

export function trackCtaClick({
  ctaId,
  ctaText,
  destination,
  placement,
  surface,
  tier,
  messageKey,
  messageKeyApplied,
  flowKey,
  flowVersion,
  editionId,
}) {
  const durableMessageKey =
    resolveDurableReportMessageKey(messageKey) || messageKey;
  const durableMessageKeyApplied =
    resolveDurableReportMessageKey(messageKeyApplied) || messageKeyApplied;

  return trackGa4Event('cta_click', {
    cta_id: ctaId,
    cta_text: ctaText,
    cta_destination: destination,
    placement,
    surface,
    tier,
    message_key: durableMessageKey,
    message_key_applied: durableMessageKeyApplied,
    flow_key: flowKey,
    flow_version: flowVersion,
    edition_id: editionId,
  });
}

// placement stays the funnel-stage dimension ('checkout_route'); ctaPlacement is
// the finer per-surface dimension added by the hosted-checkout CTAs. It is
// conditionally spread: callers that omit it produce a byte-identical params
// object to before.
export function trackBeginCheckout({
  tier,
  value = 29,
  currency = 'USD',
  placement,
  ctaPlacement,
  conversionEventId,
}) {
  return trackGa4Event('begin_checkout', {
    tier,
    value,
    currency,
    placement,
    conversion_event_id: conversionEventId,
    ...(ctaPlacement ? { cta_placement: ctaPlacement } : {}),
  });
}

export function trackCheckoutSessionCreated({
  tier,
  checkoutSessionId,
  value = 29,
  currency = 'USD',
}) {
  return trackGa4Event('checkout_session_created', {
    tier,
    checkout_session_id: checkoutSessionId,
    value,
    currency,
  });
}

export function trackCheckoutRedirect({
  tier,
  checkoutSessionId,
  destination = 'stripe_checkout',
}) {
  return trackGa4Event('checkout_redirect', {
    tier,
    checkout_session_id: checkoutSessionId,
    checkout_destination: destination,
  });
}

export function trackCheckoutCancelReturn({ tier, source, flowKey, flowVersion }) {
  return trackGa4Event('checkout_cancel_return', {
    tier,
    checkout_source: source,
    flow_key: flowKey,
    flow_version: flowVersion,
  });
}

export function trackPurchase({ tier, eventId, value, currency = 'USD' }) {
  const ga4Tracked = trackGa4Event('purchase', {
    tier,
    transaction_id: eventId,
    value,
    currency,
  });
  const googleAdsTracked = trackGoogleAdsPurchaseConversion({
    eventId,
    value,
    currency,
  });
  const microsoftUetTracked = trackMicrosoftUetPurchaseConversion({
    eventId,
    value,
    currency,
  });
  const redditPixelTracked = trackRedditPixelPurchaseConversion({
    eventId,
    value,
    currency,
  });
  return ga4Tracked || googleAdsTracked || microsoftUetTracked || redditPixelTracked;
}

export function buildCheckoutCancelUrl({
  origin,
  tier,
  source = 'checkout_route',
}) {
  const url = new URL('/pricing', origin);
  url.searchParams.set('checkout_cancel', '1');
  if (tier) url.searchParams.set('tier', tier);
  if (source) url.searchParams.set('checkout_source', source);
  return url.toString();
}
